import React from 'react';
import { Box, Typography, Container, Link } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Logo from '../assets/plannic.svg';

function Footer() {
  return (
    <Box
      component='footer'
      sx={{
        backgroundColor: '#1a1a1a',
        color: '#fff',
        mt: 'auto',
        pt: { xs: 6, md: 8 },
        pb: 'calc(12px + env(safe-area-inset-bottom))'
      }}>
      <Container maxWidth='lg'>
        {/* Logo Section - Mobile only */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            mb: 4
          }}>
          <Box
            component='img'
            src={Logo}
            alt='Plannic Logo'
            sx={{
              height: 50,
              mb: 2,
              filter: 'brightness(0) invert(1)'
            }}
          />
          <Typography
            variant='body2'
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              lineHeight: 1.8,
              textAlign: 'center',
              maxWidth: '320px'
            }}>
            Innovative Architektur und CAD-Dienstleistungen für moderne Bauprojekte.
          </Typography>
        </Box>

        {/* Main Footer Content */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: { xs: 4, md: 6 },
            mb: 6
          }}>
          {/* Company Info - Desktop only */}
          <Box sx={{ textAlign: { xs: 'center', md: 'left' }, display: { xs: 'none', md: 'block' } }}>
            <Box
              component='img'
              src={Logo}
              alt='Plannic Logo'
              sx={{
                height: 45,
                mb: 2,
                filter: 'brightness(0) invert(1)',
                display: 'inline-block'
              }}
            />
            <Typography
              variant='body2'
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                lineHeight: 1.8,
                mb: 2,
                maxWidth: '280px'
              }}>
              Innovative Architektur und CAD-Dienstleistungen für moderne Bauprojekte.
            </Typography>
          </Box>

          {/* Contact Info */}
          <Box sx={{ textAlign: { xs: 'left', md: 'left' } }}>
            <Typography
              variant='subtitle1'
              sx={{
                fontWeight: 600,
                mb: 2,
                fontFamily: 'Jura, sans-serif',
                textTransform: 'uppercase',
                fontSize: '0.875rem',
                letterSpacing: '0.1em'
              }}>
              KONTAKT
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: { xs: 'flex-start', md: 'flex-start' } }}>
              <Link
                href='tel:+41791752080'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'rgba(255, 255, 255, 0.7)',
                  textDecoration: 'none',
                  transition: 'color 0.3s ease',
                  '&:hover': {
                    color: '#fff'
                  }
                }}>
                <PhoneIcon sx={{ fontSize: '1.1rem' }} />
                <Typography variant='body2'>+41 (0) 79 175 20 80</Typography>
              </Link>
              <Link
                href='mailto:info@plannic.ch'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'rgba(255, 255, 255, 0.7)',
                  textDecoration: 'none',
                  transition: 'color 0.3s ease',
                  '&:hover': {
                    color: '#fff'
                  }
                }}>
                <EmailIcon sx={{ fontSize: '1.1rem' }} />
                <Typography variant='body2'>info@plannic.ch</Typography>
              </Link>
            </Box>
          </Box>

          {/* Address */}
          <Box sx={{ textAlign: { xs: 'left', md: 'left' } }}>
            <Typography
              variant='subtitle1'
              sx={{
                fontWeight: 600,
                mb: 2,
                fontFamily: 'Jura, sans-serif',
                textTransform: 'uppercase',
                fontSize: '0.875rem',
                letterSpacing: '0.1em'
              }}>
              ADRESSE
            </Typography>
            <Link
              href='https://maps.google.com/?q=Thurfeldstrasse+17a+9215+Schönenberg'
              target='_blank'
              rel='noopener noreferrer'
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                color: 'rgba(255, 255, 255, 0.7)',
                textDecoration: 'none',
                transition: 'color 0.3s ease',
                justifyContent: { xs: 'flex-start', md: 'flex-start' },
                '&:hover': {
                  color: '#fff'
                }
              }}>
              <LocationOnIcon sx={{ fontSize: '1.1rem', mt: 0.2 }} />
              <Box>
                <Typography variant='body2'>Thurfeldstrasse 17a</Typography>
                <Typography variant='body2'>9215 Schönenberg</Typography>
              </Box>
            </Link>
          </Box>

          {/* Quick Links - Desktop only */}
          <Box sx={{ textAlign: { xs: 'center', md: 'left' }, display: { xs: 'none', md: 'block' } }}>
            <Typography
              variant='subtitle1'
              sx={{
                fontWeight: 600,
                mb: 2,
                fontFamily: 'Jura, sans-serif',
                textTransform: 'uppercase',
                fontSize: '0.875rem',
                letterSpacing: '0.1em'
              }}>
              NAVIGATION
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
              {[
                { label: 'Architektur', href: '/architecture' },
                { label: 'CAD Services', href: '/cad-services' },
                { label: 'Projekte', href: '/services' },
                { label: 'Kontakt', href: '/contact' }
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    transition: 'color 0.3s ease, transform 0.3s ease',
                    display: 'inline-block',
                    '&:hover': {
                      color: '#fff',
                      transform: 'translateX(4px)'
                    }
                  }}>
                  {link.label}
                </Link>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Bottom Bar */}
        <Box
          sx={{
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            pt: 3,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              gap: { xs: 1, sm: 2 }
            }}>
            <Typography
              variant='body2'
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.8rem',
                textAlign: { xs: 'center', sm: 'left' }
              }}>
              © {new Date().getFullYear()} Plannic GmbH – Alle Rechte vorbehalten.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Link
              href='/privacy'
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                textDecoration: 'none',
                fontSize: '0.8rem',
                transition: 'color 0.3s ease',
                '&:hover': {
                  color: '#fff'
                }
              }}>
              Datenschutz
            </Link>
            <Link
              href='/imprint'
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                textDecoration: 'none',
                fontSize: '0.8rem',
                transition: 'color 0.3s ease',
                '&:hover': {
                  color: '#fff'
                }
              }}>
              Impressum
            </Link>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}>
            <Typography
              variant='body2'
              sx={{
                color: 'rgba(255, 255, 255, 0.4)',
                fontSize: '0.75rem'
              }}>
              Made by
            </Typography>
            <Link
              href='https://www.linkedin.com/in/edmond-saliji-ba079b1a8/'
              target='_blank'
              rel='noopener noreferrer'
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                textDecoration: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                transition: 'color 0.3s ease',
                display: 'inline-block',
                animation: 'bounce 2s ease infinite',
                '@keyframes bounce': {
                  '0%, 100%': { transform: 'translateY(0)' },
                  '20%': { transform: 'translateY(-4px)' },
                  '40%': { transform: 'translateY(0)' },
                  '60%': { transform: 'translateY(-2px)' },
                  '80%': { transform: 'translateY(0)' }
                },
                '&:hover': {
                  color: '#fff'
                }
              }}>
              EddzeraDevs
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
